import { Box, Typography } from "@mui/material";

import type { ReactNode } from "react";

interface PickerRowProps {
  /** Primary line, e.g. the human-readable label. */
  primary: string;
  /** Secondary line, e.g. the raw identifier or slug. */
  secondary: string;
  onSelect: () => void;
  /** Optional adornment rendered to the left of the text (e.g. a source badge). */
  leading?: ReactNode;
}

/**
 * Selectable picker row shared by the merge-tag and offer pickers, so the
 * keyboard/focus a11y (role, tabIndex, Enter/Space, focus-visible outline)
 * stays consistent across both.
 */
export function PickerRow({
  primary,
  secondary,
  onSelect,
  leading,
}: PickerRowProps) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 4,
        borderRadius: 1,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      })}
    >
      {leading}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {primary}
        </Typography>
        {/* Block + noWrap so long secondary text (brand copy) truncates with an ellipsis. */}
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ display: "block" }}
        >
          {secondary}
        </Typography>
      </Box>
    </Box>
  );
}
